'use client';

// Vídeo da tela da live do hero. Toca sem som só no desktop (no celular fica o poster, para não baixar
// o vídeo) e para com prefers-reduced-motion ou aba oculta. Avisa quando começa (e a cada volta, no
// modo loop) para a demonstração ficar no ritmo do vídeo.
import * as React from 'react';
import { useReducedMotion } from 'motion/react';
import { cn } from '@dropsz/ui';

export function SceneVideo({
  src,
  poster,
  loop = true,
  layout,
  onStart,
}: {
  src: string;
  poster: string | null;
  loop?: boolean;
  /** 'stream': quadro inteiro ancorado no topo (a moldura 2:1 corta o rodapé com HUD e marca d'água). */
  layout?: 'stream';
  /** Chamado quando o vídeo começa e a cada volta do loop. */
  onStart?: () => void;
}) {
  const reduce = useReducedMotion();
  const ref = React.useRef<HTMLVideoElement>(null);
  const [canPlay, setCanPlay] = React.useState(false);
  const onStartRef = React.useRef(onStart);
  onStartRef.current = onStart;

  React.useEffect(() => {
    const desktop = window.matchMedia('(min-width: 768px)');
    const update = () => setCanPlay(desktop.matches && document.visibilityState === 'visible');
    update();
    desktop.addEventListener('change', update);
    document.addEventListener('visibilitychange', update);
    return () => {
      desktop.removeEventListener('change', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  const playing = canPlay && !reduce;

  React.useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (!playing) {
      video.pause();
      return;
    }
    video.muted = true;
    // O navegador pode recusar (economia de dados): fica no poster, sem erro na tela.
    video.play().catch(() => undefined);
    let last = 0;
    const onTime = () => {
      if (video.currentTime < last) onStartRef.current?.();
      last = video.currentTime;
    };
    const onPlay = () => onStartRef.current?.();
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('play', onPlay);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('play', onPlay);
    };
  }, [playing, src]);

  return (
    <video
      ref={ref}
      aria-hidden
      src={playing ? src : undefined}
      poster={poster ?? undefined}
      muted
      loop={loop}
      playsInline
      preload="none"
      disablePictureInPicture
      className={cn('absolute inset-0 size-full object-cover', layout === 'stream' ? 'object-top' : 'object-center')}
    />
  );
}
